import isEmpty from 'lodash/isEmpty';
import validator from 'validator';
import moment from 'moment';
import { GET_INSTALLMENT_SUCCESS, GET_OFFER_PRODUCTS_AU_SUCCESS } from '../actions/actionTypes';
import { testLocalStorageSupport } from './localRepository';

const emptyDimension = '(not set)';

const getYearOfBirthFromSSN = (ssn) => {
  if (!ssn) {
    return emptyDimension;
  }
  let year = '';
  let century = '';
  if (COUNTRY === 'LV') {
    year = ssn.substring(4, 6);
    century = ssn.substring(7, 8);
    century = century === '1' ? '19' : '20';
  }
  if (COUNTRY === 'FI') {
    year = ssn.substring(4, 6);
    century = ssn.substring(6, 7);
    century = century === '-' ? '19' : '20';
  }
  if (COUNTRY === 'EE') {
    year = ssn.substring(1, 3);
    century = ssn.substring(0, 1);
    switch (century) {
      case '1':
        century = '18';
        break;
      case '2':
        century = '18';
        break;
      case '3':
        century = '19';
        break;
      case '4':
        century = '19';
        break;
      case '5':
        century = '20';
        break;
      case '6':
        century = '20';
        break;
      case '7':
        century = '21';
        break;
      case '8':
        century = '21';
        break;
      default:
    }
  }
  if (COUNTRY === 'PL') {
    year = ssn.substring(0, 2);
    const month = parseInt(ssn.substring(2, 4), 10);
    if (month <= 12) {
      century = '19';
    } else {
      century = '20';
    }
  }
  return century + year;
};

/**
 * Function to calculate sum from any array of numbers
 * @param {array} data
 * @returns {int}
 */
const calculateTotal = (data, type) => {
  let total = 0;
  let items = [];
  if (type === 'income') {
    items = [
      data.salary,
      data.benefits,
      data.pension,
      data.otherIncome,
    ];
  }
  if (type === 'expenses') {
    items = [
      data.housingCosts,
      data.livingCosts,
      data.otherLoans,
    ];
  }

  items.forEach((item) => {
    if (item && validator.isNumeric(item.toString())) {
      total += item;
    }
  });
  return `${total}`;
};

const getSubCheckout = (substep, event = 'subCheckout') => {
  const subCheckout = { event };
  if (COUNTRY === 'MX') {
    subCheckout.subcheckout = substep;
  } else {
    subCheckout.substep = substep;
  }
  return subCheckout;
};

const getAuthObject = (authentication, loan, action, eventName) => {
  let userId;
  if (action && action.result && action.result.data && action.result.data.customer) {
    userId = action.result.data.customer.id;
  }

  const event = { userId, event: eventName };

  if (COUNTRY === 'AU' && authentication.shouldReportAuthEvent && eventName === 'registerComplete') {
    return {
      custId: userId,
      pageView: 'registration_complete',
      event: 'subcheckout',
      clientType: 'New Client',
      substep: '0.2',
    };
  }

  if (COUNTRY === 'EE') {
    event.creditTarget = loan.loanPurpose;
  }

  if (['EE', 'PL', 'LV', 'ES'].includes(COUNTRY)) {
    event.event = authentication.isLoginScreen ? 'loginNewApp' : 'registrationNewApp';
    if (authentication.shouldReportAuthEvent) {
      return event;
    }
  }

  if ((COUNTRY === 'FI' || COUNTRY === 'EE') && testLocalStorageSupport()) {
    const bankLoginStarted = localStorage.getItem('bankLoginStarted');
    if (bankLoginStarted) {
      localStorage.removeItem('bankLoginStarted');
      if (moment.utc().diff(moment.utc(bankLoginStarted), 'minutes') <= 10) {
        return event;
      }
    }
  }

  return null;
};

const returnNewPage = (pageType) => ({
  event: 'newpage',
  pageType,
});

const getDimensionValue = (value) => {
  if (parseInt(value, 10)) {
    return (value / 100).toFixed(2);
  }
  if (value || value === 0) {
    return `${value}`;
  }
  return emptyDimension;
};

const getCityPlusZip = (address) => (
  !isEmpty(address) ? `${address.city}-${address.postcode}` : emptyDimension
);

const getHybridIfNeeded = (isHybrid) => {
  if (isHybrid) {
    return { hybrid: true };
  }
  return null;
};

export const detectClientType = (storeState) => {
  let clientDescription = 'Client';
  if (COUNTRY === 'PL' || COUNTRY === 'MX') {
    clientDescription = 'Customer';
  }
  const clientType = storeState.authentication.isOldCustomer
    ? `Returning ${clientDescription}`
    : `New ${clientDescription}`;
  return clientType;
};

export const isInstallment = (productType) => {
  if (PRODUCT_IL || productType === 'INSTALLMENT') {
    return true;
  }
  return null;
};

const getLTPageName = (step) => (
  {
    1: 'address',
    2: 'education',
    3: 'loanType',
    4: 'income',
    5: 'obligations',
    6: 'offer',
    7: 'legal',
    purchase: 'finpass',
  }[step] || 'uknown_page'
);

/**
 * Get page name based on step for AU GA Events
 * @returns {String} with AU GA internal page name variable
 */
const getAUPageName = (step) => (
  {
    1: 'income_verification',
    1.2: 'income_complete',
    2: 'address',
    3: 'identification',
    3.1: 'family',
    4: 'work',
    5: 'income',
    5.1: 'expenses',
    6: 'financial_commitments',
    6.1: 'waiting_offer',
    7: 'offer',
    8: 'offer',
    thankYouAU: '_thankyou_success',
  }[step] || 'uknown_page'
);

/**
 * Get product type for Google analytics. Can't change during process
 * @returns {Object} with credit type as a name and id for Google Analtycs puproses
 */
export const getCreditTypeGa = () => {
  const creditType = {
    name: PRODUCT_IL ? 'Installment' : 'Creditline',
    id: PRODUCT_IL ? '2' : '1',
  };
  if (COUNTRY === 'PL') {
    creditType.name = 'INSTALLMENT';
  }
  return creditType;
};

/**
 * Function to collect proper meta data for Google Analytics
 * @param {Object} storeState
 * @param {int|String} step - current step
 * @param {Object} data - customer entered data or other data passed to this function
 * @returns {Object} used for sending it to GA from middleware
 */
export const getAnalyticsObject = (step, storeState, data = {}) => {
  let authentication;
  let loan;
  let financialData;
  let partnerId = 'credit24';
  let clientType;
  if (storeState) {
    ({ authentication, loan, loan: { financialData } } = storeState);
    const { integration } = storeState;
    if (integration.isParamsPassed) {
      partnerId = integration.partnerID;
    }
    clientType = detectClientType(storeState);
  }

  /*
  * Constant productMeta attributes across all application steps
  */
  const creditTypeGa = getCreditTypeGa();
  let productMeta = {
    id: creditTypeGa.id,
    name: creditTypeGa.name,
    quantity: '1',
  };

  const addAdditionalMetadata = (gaEvent) => {
    if (COUNTRY === 'LT') {
      return {};
    }

    const gaObject = gaEvent;
    const isAUIncomeSubStep = COUNTRY === 'AU' && (step === '1.1' || step === '1.2');
    const isAUWaitingStep = COUNTRY === 'AU' && step === '6.1';
    if (financialData && COUNTRY !== 'MX' && COUNTRY !== 'CZ') {
      if (financialData.occupation && COUNTRY !== 'LV') {
        gaObject.incomeSource = financialData.occupation;
      }
      if (COUNTRY === 'FI' || COUNTRY === 'ES') {
        if (financialData.education) {
          gaObject.education = financialData.education;
        }
      }
      if (COUNTRY === 'FI') {
        gaObject.expenditure = getDimensionValue(calculateTotal(financialData, 'expenses'));
      }
    }
    if (loan.id) {
      if (COUNTRY === 'AU') {
        if (!isAUIncomeSubStep) {
          gaObject.appId = `${loan.id}`;
        }
      } else {
        gaObject.applicationID = `${loan.id}`;
      }
    }
    if (authentication.id) {
      if (COUNTRY === 'AU') {
        gaObject.custId = `${authentication.id}`;
      } else {
        gaObject.userId = `${authentication.id}`;
      }
    }
    if (COUNTRY === 'EE' && loan.loanPurpose) {
      gaObject.creditTarget = loan.loanPurpose;
    }

    if (COUNTRY === 'AU') {
      gaObject.pageView = getAUPageName(step);
    }

    if (!(isAUIncomeSubStep || isAUWaitingStep)) {
      gaObject.partnerID = partnerId;
    }

    if (COUNTRY === 'PL') {
      if (loan.isFilledByBroker) {
        gaObject.hybrid = true;
      }
    }
    return gaObject;
  };

  // switch steps WITHOUT productMeta first
  switch (step) {
    case 'rejectCZ': {
      const event = {
        clientType,
        event: 'reject',
        applicationID: loan.id,
        userId: authentication.id,
        product: productMeta.name,
      };
      const loanStep = loan.steps[loan.currentStep];
      if (loanStep && loanStep.analyticsStepName) {
        event.page = loanStep.analyticsStepName.replace(/CZ$/, '');
      }

      return event;
    }
    case 'getPinCodeCZ':
      return {
        event: 'getPinCode',
        page: 'legal',
        applicationID: loan.id,
        userId: authentication.id,
      };
    case 'registrationCZ':
      return {
        event: 'page',
        page: 'registration',
      };
    case 'mwTermsAndConditions':
      return {
        event: 'login',
        userId: authentication.id,
      };
    case 'verificationOneCent':
    case 'verificationBank':
      if (!authentication.id) {
        return null;
      }
      return {
        event: step,
        userId: authentication.id,
        applicationID: loan.id,
      };
    case 'creditTarget':
      return {
        event: step,
        creditTarget: loan.loanPurpose,
      };
    case 'forgot':
    case 'register':
    case 'temppassword':
    case '0':
      return returnNewPage(step);
    case 'login':
      return {
        pageView: 'login_page',
        event: 'subcheckout',
        substep: '0',
      };
    case 'registerStart':
      return {
        pageView: 'registration_form',
        event: 'subcheckout',
        clientType: 'New Client',
        substep: '0.1',
      };
    case 'registerComplete':
      return getAuthObject(authentication, loan, data, step);
    case '1.1':
    case '1.2':
    case '2.1':
    case '3.1':
    case '4.1':
    case '5.1':
    case '6.1':
    case '6.2':
    case '7.1':
    case '8.1': {
      return {
        ...addAdditionalMetadata(getSubCheckout(step)),
        clientType,
      };
    }
    case 'verificationBankMX':
      return {
        ...addAdditionalMetadata(getSubCheckout('10', 'verificationBank')),
        clientType,
      };
    case 'ThankYou':
      return {
        ...addAdditionalMetadata(getSubCheckout('11', step)),
        clientType,
      };
    case '0.*':
      return getAuthObject(authentication, loan, data);
    case 'registrationCURP':
    case 'registrationName':
      return { event: step };
    case 'startNewApp':
      return getAuthObject(authentication, loan, data, step);
    case 'vasPeace':
      return {
        event: step,
        product: loan.repaymentPlan && loan.repaymentPlan.enabled ? 'YesPeace' : 'NoPeace',
        clientType,
        applicationID: `${loan.id}`,
        userId: `${authentication.id}`,
      };
    case 'thankYou':
    case 'EC-thankYou':
      return {
        clientType,
        event: step,
        applicationID: `${loan.id}`,
        userId: `${authentication.id}`,
        product: creditTypeGa.name,
      };
    case 'thank_you':
      return {
        clientType,
        event: step,
        applicationID: loan.id,
        userId: authentication.id,
        product: creditTypeGa.name,
        ...getHybridIfNeeded(loan.isFilledByBroker),
      };
    case 'thankyou':
    case 'verificationID':
      return ({
        event: step,
        clientType,
        applicationID: `${loan.id}`,
        userId: `${authentication.id}`,
      });
    case 'verificationJUMIO':
      return ({
        event: step,
        clientType,
        applicationID: `${loan.id}`,
        userId: `${authentication.id}`,
        partnerID: partnerId,
        ...getSubCheckout('9', step),
      });
    case 'rejected':
      return {
        pageView: 'reject',
        event: 'reject',
        clientType,
        appId: `${loan.id}`,
        custId: `${authentication.id}`,
        alert_msg: `${loan.rejectedMessage}`,
      };
    case 'agreement':
    case 'pin':
    case 'completed':
      return ({
        event: 'calma',
        step,
      });
    case 'EC-duedate':
      return ({
        event: 'subcheckout',
        step,
        applicationID: `${loan.id}`,
        userId: `${authentication.id}`,
      });
    case 'slider':
      return {
        event: 'page',
        ec: step,
      };
    case 'getSMS':
    case 'bank':
    case 'verification':
    case 'confirmPending':
    case 'confirmDone':
      return {
        event: 'register',
        ec: step,
      };
    case 'wait':
      return {
        event: 'page',
        ec: step,
        applicationID: `${loan.id}`,
        clientType,
        userId: `${authentication.id}`,
      };
    case 'reject':
      return {
        event: 'reject',
        ec: `reject-${getLTPageName(loan.currentStep + 1)}`,
        applicationID: `${loan.id}`,
        clientType,
        userId: `${authentication.id}`,
        alert_msg: `${loan.rejectedMessage}`,
      };
    case 'credentials':
      return {
        ...addAdditionalMetadata({
          event: 'checkout_new',
          page: 'credentials',
          applicationID: `${loan.id}`,
          userId: `${authentication.id}`,
          product: productMeta.name,
          clientType,
        }),
      };
    default:
  }

  if (storeState && COUNTRY !== 'LT') {
    productMeta = {
      price: getDimensionValue(loan.product.principal),
      category: clientType,
      ...productMeta,
    };
    if (COUNTRY === 'FI') {
      productMeta = {
        dimension6: getDimensionValue(authentication.gender),
        dimension7: getYearOfBirthFromSSN(authentication.identifier),
        dimension8: getDimensionValue(calculateTotal(financialData, 'income')),
        dimension9: getCityPlusZip(authentication.address),
        ...productMeta,
      };
    }
    if (COUNTRY === 'EE') {
      productMeta = {
        dimension6: getDimensionValue(authentication.gender),
        dimension7: getYearOfBirthFromSSN(authentication.identifier),
        dimension8: getDimensionValue(financialData.netIncome),
        dimension9: getCityPlusZip(loan.customer.address),
        ...productMeta,
      };
    }

    if (COUNTRY === 'MX') {
      productMeta.category = clientType;
    }
  }

  // switch the rest of steps
  if (step === '8') {
    if (COUNTRY === 'FI' || COUNTRY === 'EE' || COUNTRY === 'MX') {
      productMeta.price = getDimensionValue(loan.selectedProduct.firstDrawAmount);
    }
    if (COUNTRY === 'PL' || COUNTRY === 'LV') {
      const price = loan.selectedProduct.firstDrawAmount || data.firstDrawAmount;
      productMeta.price = getDimensionValue(price);
    }
  }
  if (step === '9') {
    if (COUNTRY === 'FI' || COUNTRY === 'EE') {
      productMeta.price = getDimensionValue(loan.selectedProduct.firstDrawAmount);
    }
  }

  if (step === 'purchase' || (COUNTRY === 'AU' && step === 'thankYouAU')) {
    productMeta.price = getDimensionValue(loan.selectedProduct.firstDrawAmount);
    productMeta.metric1 = getDimensionValue(loan.product.firstDrawAmount);
    productMeta.metric2 = productMeta.price;
    const gaEvent = {
      clientType,
      event: step,
      userId: `${authentication.id}`,
      ecommerce: {
        purchase: {
          actionField: {
            id: `${loan.id}`,
            revenue: productMeta.price,
          },
          products: [productMeta],
        },
      },
    };

    if (COUNTRY === 'AU' && step === 'thankYouAU') {
      gaEvent.event = 'purchase';
      gaEvent.clientType = clientType;
      if (loan.availablePrincipalsIndex) {
        gaEvent.ecommerce.purchase.products[0].metric1 = getDimensionValue(
          loan.availablePrincipalsIndex[loan.availablePrincipalsIndex.length - 1]
        );
      }
    }

    return addAdditionalMetadata(gaEvent);
  }
  const checkoutGaEvent = {
    clientType,
    event: 'checkout',
    ecommerce: {
      checkout: {
        actionField: { step },
        products: [productMeta],
      },
    },
  };

  if (COUNTRY !== 'MX' && COUNTRY !== 'LT') {
    checkoutGaEvent.ecommerce.checkout.actionField.option = clientType;
  }

  if (COUNTRY === 'CZ') {
    // CZ tests new way of tracking without ecommerce in events
    delete checkoutGaEvent.ecommerce;

    checkoutGaEvent.product = productMeta.name;
    checkoutGaEvent.page = step.replace(/CZ$/, '');
    if (loan.selectedProduct && loan.selectedProduct.id) {
      checkoutGaEvent.CL_limit = getDimensionValue(loan.selectedProduct.principal);
      const firstDrawAmount = loan.selectedProduct.firstDrawAmount || data.firstDrawAmount;
      if (firstDrawAmount || firstDrawAmount === 0) {
        checkoutGaEvent.CL_draw = getDimensionValue(firstDrawAmount);
      }
      if (step === 'thankYouCZ') {
        const { incomeVerificationWay, identityVerificationWay } = loan;
        let incomeVerification = 'none';
        if (incomeVerificationWay === 'KONTOMATIK') {
          incomeVerification = 'konto';
        } else if (incomeVerificationWay === 'DOCUMENT_UPLOAD') {
          incomeVerification = 'manual';
        }
        let identityVerification = 'none';
        if (identityVerificationWay === 'DOCUMENT_UPLOAD') {
          identityVerification = 'manual';
        }
        checkoutGaEvent.incomeVerification = incomeVerification;
        checkoutGaEvent.identityVerification = identityVerification;
      }
    }
  }

  if (COUNTRY === 'AU') {
    if (step === '7') {
      const { type, isPromise, promiseStatus } = data;
      const promiseSuccess = isPromise && promiseStatus === 'SUCCESS';
      const gotOffer = promiseSuccess && type === GET_OFFER_PRODUCTS_AU_SUCCESS;
      checkoutGaEvent.ecommerce.checkout.actionField.option = gotOffer ? 'with offer' : 'without offer';

      if (gotOffer && data.result.data.products.length === 0) {
        return null;
      }
    }
  }

  if (COUNTRY === 'PL') {
    if (loan.isFilledByBroker && step === '7') {
      const { type, isPromise, promiseStatus } = data;
      const promiseSuccess = isPromise && promiseStatus === 'SUCCESS';
      const gotOffer = promiseSuccess && type === GET_INSTALLMENT_SUCCESS;
      checkoutGaEvent.ecommerce.checkout.actionField.option = `New customer ${gotOffer ? 'with offer' : 'without offer'}`;
    }
  }

  if (COUNTRY === 'LV') {
    if (step === '8') {
      const repaymentPlanEnabled = data.repaymentPlan === 'yes' || loan.repaymentPlan.enabled;
      checkoutGaEvent.product = repaymentPlanEnabled ? 'PeaceYes' : 'PeaceNo';
    }
  }

  return addAdditionalMetadata(checkoutGaEvent);
};
